import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  clientsApi,
  clientAddressesApi,
  accountsApi,
  type ClientDto,
  type ClientAddressDto,
  type ClientAuditDto,
  type AccountSummaryDto,
  type UpdateClientRequest,
  type UpdateClientAddressRequest,
  type CreateClientAddressRequest,
} from "@/lib/clients-accounts";
import { DetailPage } from "@/archetypes/DetailPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { AccountStatusBadge, type AccountStatusCode } from "@/components/clients-accounts";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// ComingSoonTab — placeholder for Plan II tabs
// ---------------------------------------------------------------------------

function ComingSoonTab({ note }: { note: string }) {
  return (
    <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
      <p className="text-sm">Coming in a future plan</p>
      <p className="mt-1 text-xs">{note}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

function OverviewTab({ clientId, client }: { clientId: number; client: ClientDto }) {
  const qc = useQueryClient();
  const [summary, setSummary] = useState(client.summary ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const patchMut = useMutation({
    mutationFn: (body: UpdateClientRequest) => clientsApi.patch(clientId, body),
    onSuccess: () => {
      toast.success("Saved");
      void qc.invalidateQueries({ queryKey: ["client", clientId] });
    },
  });

  // Sync summary field if client data refreshes
  useEffect(() => {
    setSummary(client.summary ?? "");
  }, [client.summary]);

  function handleSummaryBlur() {
    if (summary === (client.summary ?? "")) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      patchMut.mutate(buildUpdateRequest(client, { summary }));
    }, 800);
  }

  function handleToggle(field: keyof Pick<ClientDto, "lockCompanyName" | "showEmail" | "plainPackaging">, value: boolean) {
    patchMut.mutate(buildUpdateRequest(client, { [field]: value }));
  }

  return (
    <div className="space-y-6">
      {/* Contact details */}
      <section>
        <h3 className="mb-2 font-medium">Contact details</h3>
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <Row label="Full name" value={client.fullName} />
          <Row label="Email" value={client.emailAddress ?? "—"} />
          <Row label="CC email" value={client.ccEmailAddress ?? "—"} />
          <Row label="Phone" value={client.phone ?? "—"} />
          <Row label="Mobile" value={client.mobilePhone ?? "—"} />
          <Row label="Fax" value={client.fax ?? "—"} />
          <Row label="Branch" value={client.branch ?? "—"} />
          <Row label="Department" value={client.departmentName ?? "—"} />
        </dl>
      </section>

      {/* Company */}
      {client.companyName && (
        <section>
          <h3 className="mb-2 font-medium">Company</h3>
          <p className="text-sm">
            {client.companyId ? (
              <Link to={`/admin/companies`} className="underline text-primary">
                {client.companyName}
              </Link>
            ) : (
              client.companyName
            )}
          </p>
        </section>
      )}

      {/* Toggles */}
      <section>
        <h3 className="mb-2 font-medium">Options</h3>
        <div className="space-y-2">
          <ToggleRow
            id="ov-lock-company"
            label="Lock company name"
            checked={client.lockCompanyName}
            onChange={(v) => handleToggle("lockCompanyName", v)}
            disabled={patchMut.isPending}
          />
          <ToggleRow
            id="ov-show-email"
            label="Show email on dockets"
            checked={client.showEmail}
            onChange={(v) => handleToggle("showEmail", v)}
            disabled={patchMut.isPending}
          />
          <ToggleRow
            id="ov-plain-pkg"
            label="Plain packaging"
            checked={client.plainPackaging}
            onChange={(v) => handleToggle("plainPackaging", v)}
            disabled={patchMut.isPending}
          />
        </div>
      </section>

      {/* Summary — autosave on blur */}
      <section>
        <h3 className="mb-2 font-medium">Summary</h3>
        <textarea
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          rows={4}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          onBlur={handleSummaryBlur}
          placeholder="Notes about this client…"
        />
        {patchMut.isPending && <p className="mt-1 text-xs text-muted-foreground">Saving…</p>}
      </section>

      {/* Meta */}
      <section>
        <h3 className="mb-2 font-medium">Meta</h3>
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <Row label="Created" value={client.createdOn ? new Date(client.createdOn).toLocaleString("en-AU") : "—"} />
          <Row label="Created by" value={client.createdByName ?? "—"} />
          <Row label="Has credit account" value={client.hasApprovedCreditAccount ? "Yes" : "No"} />
          <Row label="Has website login" value={client.hasWebSiteLogin ? "Yes" : "No"} />
          <Row label="Deleted" value={client.deleted ? "Yes" : "No"} />
        </dl>
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function ToggleRow({ id, label, checked, onChange, disabled }: {
  id: string; label: string; checked: boolean;
  onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <Switch id={id} checked={checked} onCheckedChange={onChange} disabled={disabled} />
      {label}
    </label>
  );
}

function buildUpdateRequest(client: ClientDto, overrides: Partial<UpdateClientRequest>): UpdateClientRequest {
  return {
    firstName: client.firstName,
    lastName: client.lastName,
    emailAddress: client.emailAddress,
    ccEmailAddress: client.ccEmailAddress,
    phone: client.phone,
    fax: client.fax,
    mobilePhone: client.mobilePhone,
    companyId: client.companyId,
    companyName: client.companyName,
    departmentName: client.departmentName,
    branch: client.branch,
    lockCompanyName: client.lockCompanyName,
    showEmail: client.showEmail,
    plainPackaging: client.plainPackaging,
    summary: client.summary,
    deliveryAddressId: client.deliveryAddressId,
    invoiceAddressId: client.invoiceAddressId,
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// AddressesTab
// ---------------------------------------------------------------------------

function AddressesTab({ clientId }: { clientId: number }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ClientAddressDto | null>(null);
  const [newAddr, setNewAddr] = useState({ address: "", city: "", postCode: "" });
  const [showAdd, setShowAdd] = useState(false);

  const { data: addresses, isLoading } = useQuery({
    queryKey: ["client-addresses", clientId],
    queryFn: () => clientAddressesApi.list(clientId),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateClientAddressRequest }) =>
      clientAddressesApi.patch(clientId, id, body),
    onSuccess: () => {
      toast.success("Address updated");
      void qc.invalidateQueries({ queryKey: ["client-addresses", clientId] });
      setEditing(null);
    },
  });

  const createMut = useMutation({
    mutationFn: (body: CreateClientAddressRequest) =>
      clientAddressesApi.create(clientId, body),
    onSuccess: () => {
      toast.success("Address added");
      void qc.invalidateQueries({ queryKey: ["client-addresses", clientId] });
      setShowAdd(false);
      setNewAddr({ address: "", city: "", postCode: "" });
    },
  });

  const cols: DataGridColumn<ClientAddressDto>[] = [
    {
      id: "addr",
      header: "Address",
      accessor: (r) =>
        editing?.id === r.id ? (
          <Input
            defaultValue={r.address}
            onBlur={(e) =>
              patchMut.mutate({
                id: r.id,
                body: { address: e.target.value, city: r.city, state: r.state, postCode: r.postCode, clientOwnsAddress: r.clientOwnsAddress },
              })
            }
          />
        ) : (
          <button className="underline text-left" onClick={() => setEditing(r)}>
            {r.formattedAddress ?? r.address}
          </button>
        ),
    },
    { id: "city", header: "City", accessor: (r) => r.city ?? "—" },
    { id: "state", header: "State", accessor: (r) => r.state },
    { id: "postcode", header: "Post code", accessor: (r) => r.postCode ?? "—" },
  ];

  return (
    <div className="space-y-4">
      <DataGrid
        rows={(addresses ?? []).filter((a) => !a.deleted)}
        columns={cols}
        rowKey={(r) => r.id}
        totalRows={(addresses ?? []).filter((a) => !a.deleted).length}
        pageIndex={0}
        pageSize={100}
        loading={isLoading}
        onPageChange={() => {}}
        emptyHeadline="No addresses"
      />
      {showAdd ? (
        <form
          className="flex flex-wrap gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate({ address: newAddr.address, city: newAddr.city, postCode: newAddr.postCode, state: null });
          }}
        >
          <div>
            <Label htmlFor="na-addr">Address *</Label>
            <Input id="na-addr" required value={newAddr.address} onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="na-city">City</Label>
            <Input id="na-city" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="na-pc">Post code</Label>
            <Input id="na-pc" value={newAddr.postCode} onChange={(e) => setNewAddr({ ...newAddr, postCode: e.target.value })} />
          </div>
          <Button type="submit" disabled={createMut.isPending}>Add</Button>
          <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
        </form>
      ) : (
        <Button variant="outline" onClick={() => setShowAdd(true)}>+ Add address</Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AccountsTab
// ---------------------------------------------------------------------------

function AccountsTab({ clientId }: { clientId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["accounts-for-client", clientId],
    queryFn: () => accountsApi.list(`clientId=${clientId}`),
  });

  const cols: DataGridColumn<AccountSummaryDto>[] = [
    {
      id: "ref",
      header: "Reference",
      accessor: (r) => (
        <Link to={`/accounts/${r.accountId}`} className="underline text-primary">
          {r.accountReferenceNo}
        </Link>
      ),
    },
    { id: "company", header: "Company", accessor: (r) => r.companyName ?? "—" },
    {
      id: "status",
      header: "Status",
      accessor: (r) => (
        <AccountStatusBadge code={r.accountStatusCode as AccountStatusCode} />
      ),
    },
    {
      id: "balance",
      header: "Balance",
      accessor: (r) => fmt.format(r.currentBalanceOutstanding),
    },
  ];

  return (
    <DataGrid
      rows={data?.items ?? []}
      columns={cols}
      rowKey={(r) => r.accountId}
      totalRows={data?.totalCount ?? 0}
      pageIndex={0}
      pageSize={50}
      loading={isLoading}
      onPageChange={() => {}}
      emptyHeadline="No accounts"
    />
  );
}

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// HistoryTab
// ---------------------------------------------------------------------------

function HistoryTab({ clientId }: { clientId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["client-audit", clientId],
    queryFn: () => clientsApi.audit(clientId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const items = data?.items ?? [];
  if (items.length === 0) return <p className="text-sm text-muted-foreground">No history events.</p>;

  return (
    <ul className="space-y-2 text-sm">
      {items.map((a: ClientAuditDto) => (
        <li key={a.auditRecId} className="flex gap-2">
          <span className="text-muted-foreground whitespace-nowrap">
            {new Date(a.createdOn).toLocaleString("en-AU")}
          </span>
          <span>
            <strong>{a.eventType}</strong>
            {a.eventDescription ? ` — ${a.eventDescription}` : ""}
            {a.userName ? <span className="text-muted-foreground"> ({a.userName})</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// ClientDetailPage
// ---------------------------------------------------------------------------

export function ClientDetailPage() {
  const { id = "0" } = useParams();
  const clientId = Number(id);

  const { data: client } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.get(clientId),
  });

  const tabs = client
    ? [
        {
          id: "overview",
          label: "Overview",
          content: <OverviewTab clientId={clientId} client={client} />,
        },
        {
          id: "addresses",
          label: "Addresses",
          content: <AddressesTab clientId={clientId} />,
        },
        {
          id: "accounts",
          label: "Accounts",
          content: <AccountsTab clientId={clientId} />,
        },
        {
          id: "quotes",
          label: "Quotes",
          content: <ComingSoonTab note="Loaded from enquiries-and-quotes" />,
        },
        {
          id: "jobs",
          label: "Jobs",
          content: <ComingSoonTab note="Loaded from jobs-and-workflow" />,
        },
        {
          id: "emails",
          label: "Emails",
          content: <ComingSoonTab note="Loaded from email-comms" />,
        },
        {
          id: "history",
          label: "History",
          content: <HistoryTab clientId={clientId} />,
        },
      ]
    : [];

  return (
    <DetailPage
      title={
        client
          ? `${client.fullName} · ${client.companyName ?? "—"}`
          : "Loading…"
      }
      tabs={tabs}
      defaultTab="overview"
    />
  );
}
